import React, { createContext, useContext, useState, useEffect } from 'react';
import { portfolioAPI } from '../services/api';

const ThemeContext = createContext();
const DEFAULT_THEME = {
  name: 'Orange Gradient (Default)',
  colors: {
    primary: '#ff6b35',
    secondary: '#f7931e',
    background: '#0a0a0a',
    surface: '#1a1a1a',
    text: '#ffffff',
    textSecondary: '#9ca3af',
    border: '#ff6b35'
  }
};

const DEFAULT_TYPOGRAPHY = {
  name: 'Default Sans (Current)',
  headlineFont: "'Inter', system-ui, -apple-system, sans-serif",
  bodyFont: "'Inter', system-ui, -apple-system, sans-serif",
  accentFont: "'Inter', system-ui, -apple-system, sans-serif",
  baseFontSize: '16px',
  headingWeight: '700',
  bodyWeight: '400',
  headingLetterSpacing: '-0.02em',
  bodyLetterSpacing: '-0.01em',
  kickerTransform: 'uppercase'
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(null);
  const [typography, setTypography] = useState(DEFAULT_TYPOGRAPHY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchTheme(), fetchTypography()]);
      setLoading(false);
    };
    load();
  }, []);

  const fetchTheme = async () => {
    try {
      const response = await portfolioAPI.getTheme();
      if (response.success && response.data) {
        applyTheme(response.data);
        setTheme(response.data);
      }
    } catch (error) {
      console.error('Error fetching theme:', error);
      applyTheme(DEFAULT_THEME);
      setTheme(DEFAULT_THEME);
    }
  };

  const applyTheme = (themeData) => {
    const root = document.documentElement;
    const colors = themeData.colors;
    
    // Apply CSS variables
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-surface', colors.surface);
    root.style.setProperty('--color-text', colors.text);
    root.style.setProperty('--color-text-secondary', colors.textSecondary);
    root.style.setProperty('--color-border', colors.border);
  };

  const fetchTypography = async () => {
    try {
      const response = await portfolioAPI.getTypography();
      if (response.success && response.data) {
        applyTypography(response.data);
        setTypography(response.data);
      }
    } catch (error) {
      console.error('Error fetching typography:', error);
      applyTypography(DEFAULT_TYPOGRAPHY);
      setTypography(DEFAULT_TYPOGRAPHY);
    }
  };

  const applyTypography = (typographyData) => {
    const root = document.documentElement;
    root.style.setProperty('--font-heading', typographyData.headlineFont);
    root.style.setProperty('--font-body', typographyData.bodyFont);
    root.style.setProperty('--font-accent', typographyData.accentFont || typographyData.headlineFont);
    root.style.setProperty('--font-size-base', typographyData.baseFontSize || '16px');
    root.style.setProperty('--font-weight-heading', typographyData.headingWeight || '700');
    root.style.setProperty('--font-weight-body', typographyData.bodyWeight || '400');
    root.style.setProperty('--font-letter-spacing-heading', typographyData.headingLetterSpacing || '-0.03em');
    root.style.setProperty('--font-letter-spacing-body', typographyData.bodyLetterSpacing || '-0.01em');
    root.style.setProperty('--font-kicker-transform', typographyData.kickerTransform || 'uppercase');
  };

  const updateTheme = (newTheme) => {
    applyTheme(newTheme);
    setTheme(newTheme);
  };

  const updateTypography = (newTypography) => {
    applyTypography(newTypography);
    setTypography(newTypography);
  };

  const value = {
    theme,
    typography,
    loading,
    updateTheme,
    updateTypography,
    refreshTheme: fetchTheme,
    refreshTypography: fetchTypography,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
